import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "opero-setup-session";

export interface SetupSession {
  setupToken: string;
  expiresIn?: number;
}

interface SetupTokenState {
  session: SetupSession | null;
  hydrated: boolean;
}

export function clearSetupSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Reads setup session from router state on first navigation, persists to
 * sessionStorage so an F5 within the 30-min TTL keeps the user on /setup.
 * sessionStorage is tab-scoped, so closing the tab drops it.
 */
export function useSetupToken(): SetupTokenState {
  const location = useLocation();
  const stateToken = (location.state as Partial<SetupSession> | null)
    ?.setupToken;
  const [session, setSession] = useState<SetupSession | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (stateToken) {
      const fromState = location.state as Partial<SetupSession>;
      const next: SetupSession = {
        setupToken: stateToken,
        expiresIn: fromState.expiresIn,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setSession(next);
    } else {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) setSession(JSON.parse(raw) as SetupSession);
    }
    setHydrated(true);
    // Re-running only when the token in router state changes — sibling
    // search-param updates rebuild `location` but shouldn't refetch storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateToken]);

  return { session, hydrated };
}
